import { type Message, MessageContentType } from '@/features/messages';
import { ReplyComposePreview, ReplyTextPreview } from '@/features/messages/components/message-reply-preview';
import { cn } from '@/shared/utilities';
import { Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

interface MessageListItemReplyProps {
  reply?: Message;
  replyUser?: { fullName?: string };
  isOwn?: boolean;
  onClick: () => void;
}

const MessageListItemReply = memo<MessageListItemReplyProps>(({ reply, replyUser, isOwn = false, onClick }) => {
  const { t } = useTranslation();
  if (!reply) return null;

  // Handle revoked parent messages
  if (reply.isRevoked) {
    return (
      <div
        className={cn(
          'mex:!border-solid mex:!min-h-0 mex:m-2 mex:flex mex:flex-col mex:rounded-md mex:border-primary mex:border-l-[3px] mex:p-2',
          isOwn ? 'mex:!bg-white' : 'mex:!bg-gray-200'
        )}
      >
        <Typography
          variant='caption'
          color='text.secondary'
          fontWeight='bold'
          className='mex:!text-black mex:dark:!text-white'
        >
          {replyUser?.fullName || t('messageList.user')}
        </Typography>
        <Typography variant='body2' sx={{ fontStyle: 'italic', color: 'text.disabled' }} className='mex:!text-gray-500'>
          {t('messageList.recalled')}
        </Typography>
      </div>
    );
  }

  const renderReplyPreview = () => {
    switch (reply.contentType) {
      case MessageContentType.TEXT:
        return <ReplyTextPreview content={reply.content || ''} />;
      case MessageContentType.COMPOSE:
      case MessageContentType.COMBINE:
        return <ReplyComposePreview message={reply} />;
      default:
        return <ReplyTextPreview content={t('messages.reply.mediaTypes.multimedia')} />;
    }
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'mex:!border-solid mex:!min-h-0 mex:hover:!bg-gray-200 mex:m-2 mex:flex mex:flex-col mex:rounded-md mex:border-primary mex:border-l-[3px] mex:p-2',
        isOwn ? 'mex:!bg-white' : 'mex:!bg-gray-200'
      )}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      aria-label={t('messageList.viewReply')}
    >
      <Typography
        variant='caption'
        color='text.secondary'
        fontWeight='bold'
        className='mex:!text-black mex:dark:!text-white'
      >
        {replyUser?.fullName || t('messageList.user')}
      </Typography>
      {renderReplyPreview()}
    </div>
  );
});

export { MessageListItemReply };
export default MessageListItemReply;
